import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { visibilityDB } from '../components/settings/PermissionsSettings';
import { USER_ROLES } from '../types/users';

export const useVisibilityPermissions = () => {
  const { currentUser } = useAuth();
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    if (currentUser) {
      const userPermissions = visibilityDB.getVisibilityForRole(currentUser.role);
      setPermissions(userPermissions);
    } else {
      setPermissions(null);
    }
  }, [currentUser]);

  const hasAdminRights = () => {
    return currentUser && ['SUPER_ADMIN', 'ADMIN'].includes(currentUser.role);
  };

  const canAdd = (type) => {
    // Seuls les admin peuvent ajouter des données
    return hasAdminRights();
  };

  const canEdit = (type) => {
    // Seuls les admin peuvent modifier des données
    return hasAdminRights();
  };

  const canDelete = (type) => {
    // Seuls les admin peuvent supprimer des données
    return hasAdminRights();
  };

  const canViewTab = (section, tab = null) => {
    if (!currentUser) return false;

    // Tous les utilisateurs connectés peuvent voir les sections de base
    const basicSections = ['calendar', 'technicians', 'skills', 'absences', 'oncalls', 'statistics', 'planning'];
    
    if (basicSections.includes(section)) {
      return true; // Tous peuvent voir ces sections (avec leurs données filtrées)
    }

    // Sections admin uniquement
    const adminSections = ['teams', 'settings', 'users'];
    if (adminSections.includes(section)) {
      return hasAdminRights();
    }

    // Onglets spéciaux dans les statistiques
    if (section === 'statistics' && tab) {
      // Tous peuvent voir les statistiques de base, mais certains onglets sont réservés
      const adminOnlyTabs = ['admin-reports'];
      if (adminOnlyTabs.includes(tab)) {
        return hasAdminRights();
      }
      return true;
    }

    return true; // Par défaut, autoriser
  };

  const isReadOnly = () => {
    // Les utilisateurs VIEWER et TECHNICIAN sont en lecture seule
    return currentUser && ['VIEWER', 'TECHNICIAN'].includes(currentUser.role);
  };

  const canViewSidebarItem = (itemKey) => {
    if (!currentUser) return false;
    
    // SuperAdmin peut tout voir - retourner true directement
    if (currentUser.role === USER_ROLES.SUPER_ADMIN) return true;
    
    // Pour les autres rôles, vérifier les permissions
    if (!permissions) return false;
    return permissions.sidebar?.[itemKey] || false;
  };

  const getVisibleTabs = (groupKey) => {
    if (!currentUser) return [];
    
    // SuperAdmin peut tout voir - retourner tous les onglets disponibles
    if (currentUser.role === USER_ROLES.SUPER_ADMIN) {
      // Retourner une liste par défaut des onglets disponibles
      const defaultTabs = {
        statistics: ['coverage', 'technicians', 'analysis'],
        settings: ['userRights', 'teamsManagement', 'emailSettings', 'automaticEmails', 'smtpSettings', 'changePassword', 'userManagement']
      };
      return defaultTabs[groupKey] || [];
    }
    
    // Pour les autres rôles, vérifier les permissions
    if (!permissions) return [];
    const tabs = permissions.tabs?.[groupKey] || {};
    return Object.keys(tabs).filter(tabKey => tabs[tabKey]);
  };

  return {
    hasAdminRights,
    canAdd,
    canEdit,
    canDelete,
    canViewTab,
    isReadOnly,
    currentUserRole: currentUser?.role,
    canViewSidebarItem,
    getVisibleTabs,
    permissions
  };
};

export default useVisibilityPermissions; 